import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    department: '',
    useCase: '',
    contactMethod: 'email',
    timeline: '30days',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your interest! We will contact you shortly.');
  };

  return (
    <Box
      id="contact"
      sx={{
        py: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.25rem' },
            }}
          >
            Request a Demo
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 6,
              color: '#9CA3AF',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            See how VECTOR can transform your intelligence operations
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  backgroundColor: '#0A0A0A',
                  border: '1px solid #1A1A1A',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Organization"
                          name="organization"
                          value={formData.organization}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Department/Role"
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Use Case"
                          name="useCase"
                          value={formData.useCase}
                          onChange={handleChange}
                          required
                          multiline
                          rows={4}
                          variant="outlined"
                          placeholder="Please describe your use case and requirements..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Preferred Contact Method"
                          name="contactMethod"
                          value={formData.contactMethod}
                          onChange={handleChange}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        >
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="video">Video Call</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Timeline"
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleChange}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#2F3336',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1D9BF0',
                              },
                            },
                          }}
                        >
                          <MenuItem value="immediate">Immediate</MenuItem>
                          <MenuItem value="30days">30 Days</MenuItem>
                          <MenuItem value="90days">90 Days</MenuItem>
                          <MenuItem value="6months">6+ Months</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          endIcon={<SendIcon />}
                          sx={{
                            py: 1.5,
                            fontSize: '1.125rem',
                          }}
                        >
                          Submit Request
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    color: '#E7E9EA',
                    fontWeight: 600,
                  }}
                >
                  Contact Information
                </Typography>
                
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ color: '#1D9BF0', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#E7E9EA' }}>
                      info@vectoronline.us
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ color: '#1D9BF0', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#E7E9EA' }}>
                      (555) 123-4567
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ color: '#1D9BF0', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#E7E9EA' }}>
                      Your Business Address
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Card
                sx={{
                  backgroundColor: '#0A0A0A',
                  border: '1px solid #1D9BF0',
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: '#1D9BF0',
                      fontWeight: 600,
                    }}
                  >
                    Why Choose VECTOR?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#C7CDD3', mb: 2 }}>
                    ✓ CJIS v6.0 Compliant
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#C7CDD3', mb: 2 }}>
                    ✓ 99.9% Uptime SLA
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#C7CDD3', mb: 2 }}>
                    ✓ 24/7 Support
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#C7CDD3' }}>
                    ✓ Dedicated Success Manager
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactSection;